import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { FormData } from "src/interfaces/iproptypes";
import { COLOR_GREY_400, COLOR_GREY_800, COLOR_TEXT_2, COLOR_TEXT_3 } from "src/utils/colors";
import { validateEmail } from "src/utils/functions";

interface PersonalInfoProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}

const PersonalInfo: React.FC<PersonalInfoProps> = ({ formData, setFormData }) => {

  const [emailError, setEmailError] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<boolean>(false);

  const handleChange = (key: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Box sx={{ width: "100%", mt: 'auto' }}>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: 500,
          mb: 4,
          fontSize: '20px',
        }}
      >
        Please provide your contact details below and you will be contacted 
        as soon as possible.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="First Name*"
            fullWidth
            variant="outlined"
            value={formData.firstName}
            onChange={(e) => handleChange("firstName", e.target.value)}
            InputProps={{ sx: { borderRadius: 2 } }}
            error={nameError}
            onBlur={() => setNameError(!formData.firstName)}
            onFocus={() => setNameError(false)}
            helperText={nameError ? 'Please enter a valid name.' : ''}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Last Name"
            fullWidth
            variant="outlined"
            value={formData.lastName}
            onChange={(e) => handleChange("lastName", e.target.value)}
            InputProps={{ sx: { borderRadius: 2 } }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Email*"
            fullWidth
            type="email"
            variant="outlined"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            InputProps={{ sx: { borderRadius: 2 } }}
            error={emailError}
            onBlur={() => setEmailError(!validateEmail(formData.email))}
            onFocus={() => setEmailError(false)}
            helperText={emailError ? 'Please enter a valid email address.' : ''}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Phone Number*"
            fullWidth
            variant="outlined"
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            InputProps={{ sx: { borderRadius: 2 } }}            
            error={phoneError}
            onBlur={() => setPhoneError(!formData.phone || formData.phone.length < 10)}
            onFocus={() => setPhoneError(false)}
            helperText={phoneError ? 'Please enter a valid phone number.' : ''}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
          Preferred mode of contact:
        </Typography>

        <RadioGroup
          row
          value={formData.preferredContactMethod}
          onChange={(e) => handleChange("preferredContactMethod", e.target.value)}
        >
          <FormControlLabel value="text" control={<Radio />} label="Text" />
          <FormControlLabel value="call" control={<Radio />} label="Call" />
        </RadioGroup>
      </Box>

      <Box sx={{ mt: 2 }}>
        <TextField
          placeholder="Any additional information to add?"
          multiline
          minRows={3}
          fullWidth
          value={formData.additionalInfo}
          onChange={(e) => handleChange("additionalInfo", e.target.value.slice(0, 500))}
          InputProps={{ sx: { borderRadius: 2 } }}
        />
        <Typography sx={{ mt: 0.5, fontSize: "13px", color: "#666" }}>
          {formData.additionalInfo.length}/500 character limit
        </Typography>
      </Box>
    </Box>
  );
};

export default PersonalInfo;
